import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { ACCENT_COLORS, APPEARANCES } from "../../theme/themeConfig";

const APPEARANCE_ICONS = {
  light: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
  dark: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
  system: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
};

function Icon({ children, size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// Header button + popover for appearance mode and accent color. Changes apply
// immediately; ThemeProvider persists them.
const ThemeSettings = () => {
  const { appearance, accentColor, setAppearance, setAccentColor } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="theme-settings" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className="theme-settings-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="theme-settings-panel"
        aria-label="Appearance settings"
        title="Appearance"
      >
        <Icon size={20}>
          <circle cx="13.5" cy="6.5" r="1.5" />
          <circle cx="17.5" cy="10.5" r="1.5" />
          <circle cx="8.5" cy="7.5" r="1.5" />
          <circle cx="6.5" cy="12.5" r="1.5" />
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.75 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9z" />
        </Icon>
      </button>

      {open && (
        <div
          id="theme-settings-panel"
          className="theme-settings-panel"
          role="dialog"
          aria-label="Appearance settings"
        >
          <fieldset className="theme-settings-group">
            <legend className="theme-settings-label">Appearance</legend>
            <div className="theme-segmented">
              {APPEARANCES.map((option) => (
                <label
                  key={option.id}
                  className={`theme-segment${appearance === option.id ? " is-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="theme-appearance"
                    value={option.id}
                    checked={appearance === option.id}
                    onChange={() => setAppearance(option.id)}
                  />
                  <Icon>{APPEARANCE_ICONS[option.id]}</Icon>
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {appearance === "system" && (
              <p className="theme-settings-hint">Follows your device setting.</p>
            )}
          </fieldset>

          <fieldset className="theme-settings-group">
            <legend className="theme-settings-label">Accent color</legend>
            <div className="theme-swatches">
              {ACCENT_COLORS.map((option) => (
                <label
                  key={option.id}
                  className={`theme-swatch${accentColor === option.id ? " is-selected" : ""}`}
                  title={option.label}
                >
                  <input
                    type="radio"
                    name="theme-accent"
                    value={option.id}
                    checked={accentColor === option.id}
                    onChange={() => setAccentColor(option.id)}
                  />
                  <span
                    className="theme-swatch-color"
                    style={{ background: option.swatch }}
                    aria-hidden="true"
                  >
                    {accentColor === option.id && (
                      <Icon size={14}>
                        <path d="M20 6L9 17l-5-5" />
                      </Icon>
                    )}
                  </span>
                  <span className="theme-swatch-name">{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
};

export default ThemeSettings;
